import { SelectOption } from "../../../molecule/inputs/select-input"
import { CSSProperties, useMemo } from "react"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import MultiSelect from "Components/molecule/inputs/multi-select"
import SearchFormContainer from "Components/atoms/containers/search-form-container"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import useEnterSearch from "hooks/useEnterSearch"
import { IOpportunityQueryParam } from "models/opportunity"

const BRANCH_KEY: keyof IOpportunityQueryParam = "branchIds"
const DEPARTMENT_KEY: keyof IOpportunityQueryParam = "departmentIds"
const OWNER_KEY: keyof IOpportunityQueryParam = "ownerIds"
const CLIENT_KEY: keyof IOpportunityQueryParam = "clientIds"
const COL_CLASS = "col"

interface SearchOpportunitiesProps {
    handleChangeQuery: (key: string, value: any) => any
    query: IOpportunityQueryParam
    branches: SelectOption[]
    projectManagers: SelectOption[]       // projectManagers equivalent — scoped by policy
    departments?: SelectOption[]
    clients?: SelectOption[]
    handleClearSearch: () => any
    handleSearch: () => any
    style?: CSSProperties
    hide?: {
        branches?: boolean
        departments?: boolean
        owner?: boolean
        clients?: boolean
    }
    fields?: {
        branch?: string       // override query key for branch filter
        department?: string   // override query key for department filter
        owner?: string        // override query key for owner filter
        client?: string       // override query key for client filter
    }
    isSearching: boolean
}

const SearchOpportunitiesForm = ({
    query,
    handleChangeQuery,
    branches,
    projectManagers,
    departments = [],
    clients = [],
    handleClearSearch,
    handleSearch,
    hide,
    fields,
    isSearching,
}: SearchOpportunitiesProps) => {

    const { selectProps } = useEnterSearch(handleSearch)

    const branchKey = fields?.branch ?? BRANCH_KEY
    const departmentKey = fields?.department ?? DEPARTMENT_KEY
    const ownerKey = fields?.owner ?? OWNER_KEY
    const clientKey = fields?.client ?? CLIENT_KEY

    const branchValue = useMemo(() => {
        const ids: string[] = (query[branchKey] as string[]) ?? []
        return branches.filter(o => ids.includes(o.value))
    }, [query, branchKey, branches])

    const ownerValue = useMemo(() => {
        const ids: string[] = (query[ownerKey] as string[]) ?? []
        return projectManagers.filter(o => ids.includes(o.value))
    }, [query, ownerKey, projectManagers])

    const departmentValue = useMemo(() => {
        const ids: string[] = (query[departmentKey] as string[]) ?? []
        return departments.filter(o => ids.includes(o.value))
    }, [query, departmentKey, departments])

    const clientValue = useMemo(() => {
        const ids: string[] = (query[clientKey] as string[]) ?? []
        return clients.filter(o => ids.includes(o.value))
    }, [query, clientKey, clients])

    return (
        <SearchFormContainer
            inputs={
                <>
                    {!hide?.branches && branches.length > 0 && (
                        <MultiSelect
                            className={COL_CLASS}
                            label="Branch"
                            value={branchValue}
                            onChange={(selected) => handleChangeQuery(branchKey, selected.map(s => s.value))}
                            options={branches}
                            {...selectProps}
                        />
                    )}

                    {!hide?.departments && departments.length > 0 && (
                        <MultiSelect
                            className={COL_CLASS}
                            label="Department"
                            value={departmentValue}
                            onChange={(selected) => handleChangeQuery(departmentKey, selected.map(s => s.value))}
                            options={departments}
                            {...selectProps}
                        />
                    )}

                    {!hide?.owner && projectManagers.length > 0 && (
                        <MultiSelect
                            className={COL_CLASS}
                            label="Project Managers"
                            value={ownerValue}
                            onChange={(selected) => handleChangeQuery(ownerKey, selected.map(s => s.value))}
                            options={projectManagers}
                            {...selectProps}
                        />
                    )}

                    {!hide?.clients && clients.length > 0 && (
                        <MultiSelect
                            className={COL_CLASS}
                            label="Customer"
                            value={clientValue}
                            onChange={(selected) => handleChangeQuery(clientKey, selected.map(s => s.value))}
                            options={clients}
                            {...selectProps}
                        />
                    )}
                </>
            }
            clearSearchButton={
                <>
                    <PrimaryButton
                        style={{ minWidth: "150px" }}
                        disabled={isSearching}
                        onClick={handleSearch}
                    >
                        {isSearching ? "Searching..." : "Search"}
                    </PrimaryButton>
                    <SecondaryButton
                        style={{ minWidth: "100px" }}
                        disabled={isSearching}
                        onClick={handleClearSearch}
                    >
                        Clear Search
                    </SecondaryButton>
                </>
            }
        />
    )
}

export default SearchOpportunitiesForm